import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const [role, setRole] = useState("player");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const u = await register({ ...form, role });
      navigate(u.role === "coach" ? "/coach" : "/onboarding");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-ink grain">
      <form onSubmit={submit} className="w-full max-w-md ct-card p-8 fade-up" data-testid="register-form">
        <div className="flex items-center justify-between">
          <Logo />
          <LanguageToggle />
        </div>
        <h1 className="font-head font-bold text-3xl mt-6 mb-1">{t("Create your account")}</h1>
        <p className="text-slate-500 text-sm mb-6">{t("Start tracking in under a minute.")}</p>

        <div className="grid grid-cols-2 gap-3 mb-5">
          {["player", "coach"].map((r) => (
            <button key={r} type="button" onClick={() => setRole(r)} data-testid={`role-${r}`}
              className={`rounded-xl border-2 p-4 text-left transition-all ${role === r ? "border-brand bg-brand/5" : "border-slate-200 hover:border-slate-300"}`}>
              <div className="font-head font-bold text-lg capitalize">{t(r)}</div>
              <div className="text-xs text-slate-500">{r === "player" ? t("Log & analyze your game") : t("Coach linked players")}</div>
            </button>
          ))}
        </div>

        {error && <div className="mb-4 text-sm text-brand bg-brand/10 rounded-lg p-3" data-testid="register-error">{error}</div>}
        <div className="space-y-4">
          <div><Label htmlFor="name">{t("Full name")}</Label><Input id="name" value={form.name} onChange={set("name")} required data-testid="register-name" className="mt-1" /></div>
          <div><Label htmlFor="email">{t("Email")}</Label><Input id="email" type="email" value={form.email} onChange={set("email")} required data-testid="register-email" className="mt-1" /></div>
          <div><Label htmlFor="phone">{t("Phone (optional)")}</Label><Input id="phone" value={form.phone} onChange={set("phone")} data-testid="register-phone" className="mt-1" placeholder="+91…" /></div>
          <div><Label htmlFor="password">{t("Password")}</Label><Input id="password" type="password" value={form.password} onChange={set("password")} required minLength={6} data-testid="register-password" className="mt-1" placeholder="Min 6 characters" /></div>
        </div>
        <Button type="submit" disabled={busy} className="w-full mt-6 bg-brand hover:bg-brand-dark h-11" data-testid="register-submit">
          {busy ? "…" : t("Create account")}
        </Button>
        <p className="text-center text-sm text-slate-500 mt-4">
          {t("Already have an account?")} <Link to="/login" className="text-brand font-semibold" data-testid="go-login">{t("Log in")}</Link>
        </p>
      </form>
    </div>
  );
}
